import { supabase } from './supabase'
import type { SyncRunningTotals } from './crm'

// Input shape for upsert_person_activity. The RPC parameter names have
// leading underscores to avoid collision with column names inside the SQL
// function; we build the params object explicitly here so callers stay
// camelCase on this side.
export interface UpsertPersonActivityInput {
  profileUrn: string
  displayName: string | null
  linkedinSlug: string | null
  totals: SyncRunningTotals
}

// Forwards a sync's running totals to Supabase. Atomic merge on the server:
// counts are additive, recency fields use greatest/least semantics, identity
// fields only update when their companion date advances. Throws on auth or
// RPC failure — callers decide whether to surface via toast or swallow.
export async function upsertPersonActivity(input: UpsertPersonActivityInput) {
  const { profileUrn, displayName, linkedinSlug, totals } = input
  if (!profileUrn) throw new Error('profileUrn required')

  // The RPC expects timestamps, not epoch ms. Convert once here so the SQL
  // side can just use timestamptz types directly.
  const toIso = (ms: number | null | undefined): string | null => {
    if (ms == null || ms === 0) return null
    try {
      return new Date(ms).toISOString()
    } catch {
      return null
    }
  }

  // The generated RPC arg types declare these as non-null, but the SQL
  // function's timestamptz params reject `''` with 'invalid input syntax for
  // type timestamp with time zone'. And for the text params, '' is NOT a
  // semantic-equivalent substitute for NULL — the SQL function treats NULL as
  // "no change" (via COALESCE) but treats '' as a real empty value, blowing
  // away a good existing display_name / linkedin_slug.
  //
  // So we pass real NULLs over the wire and cast past the generated types.
  // When we land the `upsert_person_activity` migration with proper nullable
  // params and regenerate types, this cast can go away. Tracked in the TODO
  // "Create upsert_person_activity + upsert_person_invite_event migrations".
  const params: Record<string, string | number | null> = {
    _profile_urn: profileUrn,
    _display_name: displayName,
    _linkedin_slug: linkedinSlug,
    _sent_messages_delta: totals.sentMessages,
    _received_messages_delta: totals.receivedMessages,
    _latest_sent_at: toIso(totals.latestSentAt),
    _latest_sent_by: totals.latestSentBy,
    _latest_received_at: toIso(totals.latestReceivedAt),
    _latest_received_by: totals.latestReceivedBy,
    _latest_timestamp: toIso(totals.latestTimestamp),
    _latest_direction: totals.latestDirection,
    _first_message_at: toIso(totals.firstMessageAt),
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.rpc as any)('upsert_person_activity', params)

  if (error) {
    // Preserve the Supabase error shape so callers can inspect .code / .details
    const err = new Error(`upsert_person_activity failed: ${error.message}`)
    ;(err as Error & { cause?: unknown }).cause = error
    throw err
  }
}

// --- Invitation events (Phase 4b) ---

export type InviteEventType = 'sent' | 'accepted' | 'withdrawn' | 'declined' | 'expired'

export interface UpsertPersonInviteEventInput {
  profileUrn: string
  displayName: string | null
  linkedinSlug: string | null
  eventType: InviteEventType
  eventAt: number                       // epoch ms
  counterpartyName: string | null       // for invite_inviter / invite_sent_by
}

// Forwards a single invitation event (sent / accepted / withdrawn / declined)
// to Supabase. Updates the invite-related columns on linkedin_person_activity
// with state-transition semantics (Accepted is terminal; others last-wins).
// Throws on auth or RPC failure.
export async function upsertPersonInviteEvent(input: UpsertPersonInviteEventInput) {
  const { profileUrn, displayName, linkedinSlug, eventType, eventAt, counterpartyName } = input
  if (!profileUrn) throw new Error('profileUrn required')
  if (!eventType) throw new Error('eventType required')

  // See comment in `upsertPersonActivity` — pass real NULLs, not ''.
  const params: Record<string, string | null> = {
    _profile_urn: profileUrn,
    _display_name: displayName,
    _linkedin_slug: linkedinSlug,
    _event_type: eventType,
    _event_at: new Date(eventAt ?? Date.now()).toISOString(),
    _counterparty_name: counterpartyName,
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.rpc as any)('upsert_person_invite_event', params)

  if (error) {
    const err = new Error(`upsert_person_invite_event failed: ${error.message}`)
    ;(err as Error & { cause?: unknown }).cause = error
    throw err
  }
}
